/**
 * FoxtrotLogger
 *
 * Sends events to the backend API via /pg-ingestion/client/v1/events/batch.
 * Implements ILogger interface from shared EventManager.
 */

import { ILogger, ILoggerConfig, IEvent } from '@shared/services/eventManager';
import {
	EVENTS_FLUSH_INTERVAL,
	EVENTS_MAX_QUEUE_SIZE,
} from '@shared/constants';

import { ApiService } from './Api.service';
import { DataShareService } from './DataShare.service';

export interface IFoxtrotLoggerConfig extends ILoggerConfig {
	source?: string;
	meta?: Record<string, unknown>;
}

const SENSITIVE_FIELDS = [
	'accountNumber',
	'ifsc',
	'cardNumber',
	'cvv',
	'pin',
	'password',
	'email',
	'phone',
	'mobile',
] as const;

/** Max length for string values in event data to prevent large payloads */
const MAX_STRING_LENGTH = 500;

/**
 * Sanitize event data to prevent PII leakage
 * Removes sensitive fields and limits string lengths
 */
const sanitizeEventData = (
	data: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined => {
	if (!data || typeof data !== 'object') return data;

	const sanitized: Record<string, unknown> = {};

	Object.keys(data).forEach((key) => {
		// Skip sensitive fields
		if (SENSITIVE_FIELDS.includes(key as (typeof SENSITIVE_FIELDS)[number])) {
			return;
		}

		const value = data[key];

		if (typeof value === 'string' && value.length > MAX_STRING_LENGTH) {
			sanitized[key] = `${value.substring(0, MAX_STRING_LENGTH)}...`;
		} else if (typeof value === 'object' && value !== null) {
			sanitized[key] = sanitizeEventData(value as Record<string, unknown>);
		} else {
			sanitized[key] = value;
		}
	});

	return sanitized;
};

/** Default configuration */
const DEFAULT_CONFIG: Partial<IFoxtrotLoggerConfig> = {
	batchSize: EVENTS_MAX_QUEUE_SIZE,
	flushMs: EVENTS_FLUSH_INTERVAL,
	source: 'PAYLINK',
};

/** Order type for paylink */
export const PAYLINK_ORDER_TYPE = 'PAYLINK';

/**
 * Creates a FoxtrotLogger instance for backend event logging
 *
 * @param config - Logger configuration
 * @returns ILogger instance
 */
export const createFoxtrotLogger = (
	config: IFoxtrotLoggerConfig,
): ILogger<IFoxtrotLoggerConfig> => {
	const finalConfig: IFoxtrotLoggerConfig = {
		...DEFAULT_CONFIG,
		...config,
	};

	return {
		config: finalConfig,

		process(event: IEvent): IEvent {
			const orderId = DataShareService.paylinkDetail?.orderId ?? '';

			return {
				...event,
				data: sanitizeEventData({
					...event.data,
					source: finalConfig.source,
					orderId,
					...finalConfig.meta,
				}),
			};
		},

		/**
		 * Send events to backend using ApiService.pushEvents
		 * Non-blocking, fire-and-forget
		 */
		async send(events: IEvent[]): Promise<void> {
			const orderId = DataShareService.paylinkDetail?.orderId ?? '';
			const transactUrl = `/paylink/${orderId}`;

			const payload = {
				source: 'WEB',
				events: events.map((e) => ({
					eventName: e.name,
					eventTime: e.time,
					data: e.data,
				})),
				orderType: PAYLINK_ORDER_TYPE,
				transactUrl,
			};

			try {
				await ApiService.pushEvents(payload);
			} catch (error) {
				console.error('[FoxtrotLogger] Failed to send events:', error);
			}
		},
	};
};

export const FOXTROT_LOGGER_ID = 'foxtrot';
